import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheck, FaChevronDown } from 'react-icons/fa';
import './Services.css';
import { Helmet } from 'react-helmet';

const Membership = () => {
  const [activeFaq, setActiveFaq] = useState(null);

  const memberships = [
    {
      name: 'Basic',
      tagline: 'Perfect for casual players',
      price: '3,000',
      period: '/month',
      features: [
        'Access to 2 sessions per week',
        'Group coaching (10-12 per batch)',
        'Basic fitness guidance',
        'Locker room access',
        'Equipment rental discount (10%)',
      ],
      featured: false
    },
    {
      name: 'Premium',
      tagline: 'Most Popular Choice',
      price: '7,500',
      period: '/month',
      features: [
        'Access to 4 sessions per week',
        'Group coaching (6-8 per batch)',
        'Full fitness center access',
        'Video analysis sessions',
        'Tournament preparation',
        'Equipment rental discount (20%)',
        'Priority court booking',
        'Nutrition guidance',
      ],
      featured: true
    },
    {
      name: 'Elite',
      tagline: 'For serious competitors',
      price: '15,000',
      period: '/month',
      features: [
        'Unlimited sessions per week',
        'Small group/personal coaching',
        '24/7 fitness center access',
        'Weekly video analysis',
        'Competition coaching',
        'Free equipment rental',
        'Priority court booking',
        'Sports nutrition plan',
        'Physiotherapy sessions',
        'Mental conditioning',
      ],
      featured: false
    },
  ];

  const faqs = [
    {
      question: 'Who can join Imperial Badminton Academy?',
      answer: 'Imperial Badminton Academy is open to players of all age groups and skill levels. Whether you are a beginner learning badminton for the first time, an intermediate player improving your skills, or an advanced athlete preparing for competitions, our academy offers structured training suitable for everyone.'
    },
    {
      question: 'Do I need prior badminton experience to join?',
      answer: 'No prior experience is required. Our beginner programs are specially designed for newcomers. Coaches focus on fundamentals such as grip, footwork, strokes, and rules, ensuring a strong foundation before moving to advanced techniques.'
    },
    {
      question: 'What age groups do you train?',
      answer: 'We train children, teenagers, and adults. Special attention is given to youth and junior players for early skill development, while adult and competitive players receive focused performance-based training.'
    },
    {
      question: 'Are the coaches certified and experienced?',
      answer: 'Yes, all our coaches are trained and experienced professionals with in-depth knowledge of badminton. They follow a structured coaching methodology and provide personalized guidance to ensure consistent player improvement.'
    },
    {
      question: 'What type of courts and facilities are available?',
      answer: 'Imperial Badminton Academy offers international-standard indoor courts with quality flooring, proper lighting, and modern training equipment. Our facilities are designed to ensure safety, comfort, and high-performance training.'
    },
    {
      question: 'Do you provide individual or private coaching?',
      answer: 'Yes, we offer private and one-to-one coaching sessions for players who want personalized attention. These sessions are tailored to focus on individual weaknesses, skill improvement, and specific performance goals.'
    },
    {
      question: 'How often are training sessions conducted?',
      answer: 'Training schedules vary depending on the selected program and skill level. Players can choose from regular weekly sessions to intensive training plans. Detailed schedules are provided at the time of enrollment.'
    },
    {
      question: 'Is fitness training included in the program?',
      answer: 'Yes, fitness and conditioning are an integral part of our training. Sessions include agility drills, strength training, endurance workouts, and flexibility exercises to enhance overall athletic performance and reduce injury risks.'
    },
    {
      question: 'Do you prepare players for tournaments?',
      answer: 'Yes, we actively prepare players for local, district, state, and higher-level tournaments. Tournament preparation includes match practice, strategy sessions, fitness conditioning, mental training, and performance evaluation.'
    },
    {
      question: 'Is badminton training safe for children?',
      answer: 'Absolutely. Player safety is our top priority. All training sessions are conducted under expert supervision in a safe and disciplined environment. Training intensity is adjusted according to age and physical ability.'
    },
    {
      question: 'How do you monitor player progress?',
      answer: 'We regularly assess players through performance evaluations, match practice sessions, and coach feedback. Parents and players are informed about progress, strengths, and areas that need improvement.'
    },
  ];

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <title>Membership</title>
      </Helmet>
      <div className="membership-page">
        <div className="page-header">
          <div className="container">
            <h1>Membership Plans</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/services">Services</Link>
              <span>/</span>
              <span>Membership</span>
            </div>
          </div>
        </div>

        <section className="membership-section section">
          <div className="container">
            <div className="section-title">
              <span className="section-subtitle">Join Us</span>
              <h2>Choose Your Membership</h2>
              <p>
                Flexible plans designed to match your goals and commitment level.
                All memberships include access to our world-class facilities.
              </p>
            </div>
            <div className="membership-grid">
              {memberships.map((plan, index) => (
                <div key={index} className={`membership-card ${plan.featured ? 'featured' : ''}`}>
                  {plan.featured && <div className="membership-badge">Most Popular</div>}
                  <div className="membership-header">
                    <h3>{plan.name}</h3>
                    <p>{plan.tagline}</p>
                  </div>
                  <div className="membership-price">
                    <span className="amount">₹{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                  <div className="membership-features">
                    <ul>
                      {plan.features.map((feature, idx) => (
                        <li key={idx}>
                          <FaCheck />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="membership-footer">
                    <Link to="/contact" className={`btn ${plan.featured ? 'btn-accent' : 'btn-primary'}`}>
                      Get Started <FaArrowRight />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <p style={{ textAlign: 'center', marginTop: '30px', color: 'var(--text-light)' }}>
              Looking for annual plans? Get 2 months free with yearly subscription!
            </p>
          </div>
        </section>

        <section className="comparison-section section bg-light">
          <div className="container">
            <div className="section-title">
              <h2>Plan Comparison</h2>
            </div>
            <div className="schedule-table">
              <table>
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Basic</th>
                    <th>Premium</th>
                    <th>Elite</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Sessions per week</td>
                    <td>2</td>
                    <td>4</td>
                    <td>Unlimited</td>
                  </tr>
                  <tr>
                    <td>Batch size</td>
                    <td>10-12</td>
                    <td>6-8</td>
                    <td>4 or 1-on-1</td>
                  </tr>
                  <tr>
                    <td>Fitness center</td>
                    <td>-</td>
                    <td>Full access</td>
                    <td>24/7 access</td>
                  </tr>
                  <tr>
                    <td>Video analysis</td>
                    <td>-</td>
                    <td>Monthly</td>
                    <td>Weekly</td>
                  </tr>
                  <tr>
                    <td>Tournament prep</td>
                    <td>-</td>
                    <td><FaCheck className="check" /></td>
                    <td><FaCheck className="check" /></td>
                  </tr>
                  <tr>
                    <td>Nutrition guidance</td>
                    <td>-</td>
                    <td>Basic</td>
                    <td>Full plan</td>
                  </tr>
                  <tr>
                    <td>Physiotherapy</td>
                    <td>-</td>
                    <td>-</td>
                    <td><FaCheck className="check" /></td>
                  </tr>
                  <tr>
                    <td>Equipment discount</td>
                    <td>10%</td>
                    <td>20%</td>
                    <td>Free rental</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="faq-section section">
          <div className="container">
            <div className="section-title">
              <h2>Frequently Asked Questions</h2>
            </div>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div key={index} className={`faq-item ${activeFaq === index ? 'active' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq(index)}>
                    <span>{faq.question}</span>
                    <FaChevronDown />
                  </div>
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="services-cta section bg-light">
          <div className="container">
            <div className="cta-box">
              <h2>Still Have Questions?</h2>
              <p>
                Our team is here to help you choose the right membership.
                Get in touch for personalized assistance.
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-accent">
                  Contact Us <FaArrowRight />
                </Link>
                <a href="tel:+919876543210" className="btn btn-secondary">
                  Call: +91 98765 43210
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Membership;
